import styled from "@emotion/styled"
import { Link } from "utils"
import { breakpoints } from "../../theme/mediaQueries"
import { sizes } from "../../theme/sizes"
import { colors } from "../../theme/colors"

export const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1300;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: ${sizes.spacing[80]}px;

  @media (max-width: ${breakpoints.lg - 1}px) {
    padding: ${sizes.spacing[16]}px;
    padding-top: ${sizes.spacing[64]}px;
  }
`

export const SearchContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${sizes.spacing[24]}px;
  align-items: flex-start;
  max-width: 992px;
  width: 100%;

  @media (max-width: ${breakpoints.lg - 1}px) {
    flex-direction: column;
    gap: ${sizes.spacing[24]}px;
    max-width: 100%;
  }
`

const CloseButtonBase = styled.button`
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  min-width: 56px;
  background: ${colors.primitives.neutral0};
  border: 1px solid ${colors.primitives.neutral200};
  border-radius: ${sizes.borderRadius[12]}px;
  padding: ${sizes.spacing[12]}px;
  cursor: pointer;
  color: ${colors.primitives.neutral600};
  flex-shrink: 0;

  svg {
    width: 24px;
    height: 24px;
  }

  &:hover {
    opacity: 0.7;
  }
`

export const CloseButton = styled(CloseButtonBase)`
  display: none;
  @media (min-width: ${breakpoints.lg}px) {
    display: flex;
  }
`

export const MobileCloseButton = styled(CloseButtonBase)`
  display: flex;
  @media (min-width: ${breakpoints.lg}px) {
    display: none;
  }
`

export const SearchPanel = styled.div<{ $expanded?: boolean }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow: hidden;
  /* Height hugs the content: just the input bar until a search is active, then
     it grows to hold the results, capped to the viewport so it scrolls rather
     than becoming a giant empty box (EGDE-412). */
  max-height: calc(100dvh - ${sizes.spacing[80] * 2}px);

  /* Desktop keeps the input inside a white rounded box at all times. Once a
     search is active the box opens up to the design's full window height
     instead of hugging a short empty state (EGDE-412). */
  @media (min-width: ${breakpoints.lg}px) {
    border-radius: ${sizes.borderRadius[16]}px;
    border: 1px solid ${colors.primitives.neutral200};
    background: ${colors.primitives.neutral0};
    ${({ $expanded }) => ($expanded ? "min-height: 500px;" : "")}
  }

  /* Mobile shows no wrapping box until a search begins — the input and close
     button float on their own; the panel box only appears with results. */
  @media (max-width: ${breakpoints.lg - 1}px) {
    max-height: calc(100dvh - ${sizes.spacing[64] + sizes.spacing[16] * 2}px);
    ${({ $expanded }) =>
      $expanded
        ? `
      border-radius: ${sizes.borderRadius[16]}px;
      border: 1px solid ${colors.primitives.neutral200};
      background: ${colors.primitives.neutral0};
      min-height: 480px;
    `
        : ""}
  }
`

export const SearchInputWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${sizes.spacing[16]}px;
  align-items: center;

  @media (min-width: ${breakpoints.lg}px) {
    gap: 0;
  }
`

export const StyledInput = styled.input`
  width: 100%;
  height: 56px;
  border: 1px solid ${colors.primitives.neutral200};
  padding: ${sizes.spacing[12]}px;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  color: ${colors.primitives.neutral600};
  background: ${colors.primitives.neutral0};
  outline: none;
  text-align: start;
  font-family: inherit;

  &::placeholder {
    color: ${colors.primitives.neutral400};
  }

  &::-webkit-search-cancel-button {
    -webkit-appearance: none;
  }

  @media (min-width: ${breakpoints.lg}px) {
    border: none;
    border-bottom: 1px solid ${colors.primitives.neutral200};
    border-radius: 0;
  }

  @media (max-width: ${breakpoints.lg - 1}px) {
    border-radius: ${sizes.borderRadius[12]}px;
    flex: 1;
    min-width: 0;
  }
`

export const ResultsWrapper = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${sizes.spacing[16]}px 0 ${sizes.spacing[32]}px;
  background: ${colors.primitives.neutral0};
`

export const ResultCountText = styled.div`
  padding: ${sizes.spacing[8]}px ${sizes.spacing[16]}px;
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
  color: ${colors.primitives.neutral600};
  text-align: start;

  @media (min-width: ${breakpoints.lg}px) {
    font-size: 16px;
    line-height: 24px;
  }
`

export const ResultItem = styled.div`
  background: ${colors.primitives.neutral0};
  border-bottom: 1px solid ${colors.primitives.neutral100};
  padding: ${sizes.spacing[16]}px 0;

  & > a {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: ${sizes.spacing[12]}px;
    text-decoration: none;
    color: inherit;
    width: 100%;
    padding: 0 ${sizes.spacing[12]}px;
  }

  @media (min-width: ${breakpoints.lg}px) {
    padding: ${sizes.spacing[12]}px;

    & > a {
      gap: ${sizes.spacing[16]}px;
      padding: 0;
    }
  }
`

export const ResultInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${sizes.spacing[16]}px;
  flex: 1;
  min-width: 0;
`

export const ResultTitle = styled.div`
  font-size: 18px;
  font-weight: 700;
  line-height: 26px;
  color: ${colors.primitives.neutral600};
  text-align: start;

  @media (min-width: ${breakpoints.lg}px) {
    font-size: 20px;
    line-height: 28px;
  }
`

export const ResultDescription = styled.div`
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  color: ${colors.primitives.neutral600};
  text-align: start;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (min-width: ${breakpoints.lg}px) {
    font-size: 18px;
    line-height: 26px;
    -webkit-line-clamp: 2;
  }
`

export const ResultThumbnail = styled.img`
  width: 73px;
  height: 72px;
  border-radius: 6px;
  border: 1px solid ${colors.primitives.neutral100};
  object-fit: cover;
  flex-shrink: 0;

  @media (min-width: ${breakpoints.lg}px) {
    width: 106px;
    height: 104px;
    border-radius: ${sizes.borderRadius[8]}px;
  }
`

export const ResultThumbnailFallback = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 73px;
  height: 72px;
  border-radius: 6px;
  border: 1px solid ${colors.primitives.neutral100};
  background: ${colors.primitives.neutral100};
  flex-shrink: 0;

  svg {
    width: 44px;
    height: 44px;
  }

  @media (min-width: ${breakpoints.lg}px) {
    width: 106px;
    height: 104px;
    border-radius: ${sizes.borderRadius[8]}px;

    svg {
      width: 60px;
      height: 60px;
    }
  }
`

export const ArrowIconWrapper = styled.div`
  display: none;
  align-items: center;
  justify-content: center;
  width: ${sizes.iconSizes.sm}px;
  height: ${sizes.iconSizes.sm}px;
  flex-shrink: 0;
  color: ${colors.primitives.neutral600};

  svg {
    /* EGDE-410 (round 2): keep the icon's intrinsic 7:16 proportions at its
       natural size and the icon's own 2px stroke — matching the Figma weight.
       The earlier 1.5px-stroke override drew a sub-pixel diagonal that
       anti-aliased into a fuzzy, "pixelated" line on 1x displays. */
    width: auto;
    height: ${sizes.iconSizes.sm}px;
  }

  @media (min-width: ${breakpoints.lg}px) {
    display: flex;
  }

  [dir="ltr"] & svg {
    /* @noflip */
    transform: scaleX(-1);
  }
`

export const EmptyStateWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: ${sizes.spacing[48]}px ${sizes.spacing[24]}px;
  gap: ${sizes.spacing[16]}px;
  flex: 1;
`

export const EmptyStateHeading = styled.div`
  font-size: 20px;
  font-weight: 700;
  line-height: 28px;
  color: ${colors.primitives.neutral600};

  @media (min-width: ${breakpoints.lg}px) {
    font-size: 24px;
    line-height: 32px;
  }
`

export const EmptyStateSubtitle = styled.div`
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  color: ${colors.primitives.neutral500};

  @media (min-width: ${breakpoints.lg}px) {
    font-size: 18px;
    line-height: 26px;
  }
`

export const BackToHomeButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 44px;
  padding: 0 ${sizes.spacing[32]}px;
  border-radius: ${sizes.borderRadius.full}px;
  border: 2px solid ${colors.primitives.primary500};
  background: transparent;
  color: ${colors.primitives.neutral600};
  font-size: 16px;
  font-weight: 700;
  line-height: 1;
  text-decoration: none;
  cursor: pointer;
  transition:
    background 0.2s,
    color 0.2s;

  &:hover {
    background: ${colors.primitives.primary500};
    color: ${colors.primitives.neutral0};
  }
`

export const LoadingWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${sizes.spacing[48]}px;
  flex: 1;
`
